import React, { useEffect, useState, useCallback } from 'react'
import { Table, Button, Input, Modal, Form, Rate, Space, message, Typography, Tag, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { supplierApi } from '../../services/api'

const { Title } = Typography

export default function Suppliers() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, keyword: '' })

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await supplierApi.list(query)
      setData(res.data)
      setTotal(res.total)
    } catch {
      message.error('加载供应商列表失败')
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      if (editing) {
        await supplierApi.update(editing.id, values)
        message.success('供应商更新成功')
      } else {
        await supplierApi.create(values)
        message.success('供应商创建成功')
      }
      setModalOpen(false)
      setEditing(null)
      form.resetFields()
      fetchData()
    } catch (e) {
      if (e.errorFields) return // 表单验证错误
      message.error(e?.error || '操作失败')
    }
  }

  const handleDelete = async (id) => {
    try {
      await supplierApi.delete(id)
      message.success('供应商已删除')
      fetchData()
    } catch {
      message.error('删除失败')
    }
  }

  const columns = [
    { title: '供应商名称', dataIndex: 'name', key: 'name' },
    { title: '联系人', dataIndex: 'contact', key: 'contact', render: (v) => v || '—' },
    { title: '电话', dataIndex: 'phone', key: 'phone', render: (v) => v || '—' },
    {
      title: '主营产品', dataIndex: 'products', key: 'products',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    {
      title: '评级', dataIndex: 'rating', key: 'rating', width: 180,
      render: (v) => <Rate disabled value={v} />,
    },
    {
      title: '操作', key: 'actions', width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditing(r)
            form.setFieldsValue(r)
            setModalOpen(true)
          }}>编辑</Button>
          <Popconfirm title="确定删除此供应商？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>供应商管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          setEditing(null)
          form.resetFields()
          setModalOpen(true)
        }}>新增供应商</Button>
      </div>

      <Input.Search
        placeholder="搜索供应商名称/联系人/电话/主营产品"
        allowClear
        style={{ width: 320, marginBottom: 16 }}
        onSearch={(v) => setQuery((q) => ({ ...q, keyword: v, page: 1 }))}
      />

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 个供应商`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />

      <Modal
        title={editing ? '编辑供应商' : '新增供应商'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields() }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="如：华为总代" />
          </Form.Item>
          <Form.Item name="contact" label="联系人">
            <Input placeholder="联系人姓名" />
          </Form.Item>
          <Form.Item name="phone" label="电话">
            <Input placeholder="联系电话" />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input placeholder="电子邮箱" />
          </Form.Item>
          <Form.Item name="products" label="主营产品">
            <Input placeholder="主营产品描述" />
          </Form.Item>
          <Form.Item name="rating" label="评级" initialValue={3}>
            <Rate />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={2} placeholder="备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
